import { useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, Building2, Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api, API, apiError, coverSrc } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function PreviewButton({ product }) {
  const [open, setOpen] = useState(false);
  if (product.product_type !== "streaming" || !product.has_video) return null;
  return (
    <>
      <button
        data-testid="preview-video-btn"
        onClick={() => setOpen(true)}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-ciel bg-sky-50 px-5 py-3 text-sm font-semibold text-ciel transition-colors duration-200 hover:bg-sky-100"
      >
        <PlayCircle size={17} /> Regarder l'extrait gratuit (60 s)
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Extrait gratuit — {product.title}</DialogTitle></DialogHeader>
          {open && (
            <video controls autoPlay playsInline className="w-full rounded-lg bg-black" data-testid="preview-player"
              src={`${API}/preview/${product.product_id}`} />
          )}
          <p className="text-xs text-slate-400">Extrait limité à 60 secondes. Achetez la formation pour un accès complet et illimité.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function QuoteButton({ product }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const initialMessage = `Service souhaité : ${product.title}\n\n`;
  const [form, setForm] = useState({ establishment: "", contact_name: "", email: "", phone: "", qty: 5, message: initialMessage });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotes", { ...form, product_id: product.product_id });
      toast.success("Demande de devis envoyée ! Le vendeur vous recontacte rapidement.");
      setOpen(false);
      setForm({ establishment: "", contact_name: "", email: "", phone: "", qty: 5, message: "" });
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="quote-btn"
        onClick={() => setOpen(true)}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy px-5 py-3 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
      >
        <Building2 size={16} /> Établissement ? Demander un devis
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="quote-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Devis établissement</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            Cabinets, cliniques et hôpitaux : décrivez votre besoin en licences « {product.title} ».
          </p>
          <form onSubmit={submit} className="mt-2 space-y-3">
            <Input data-testid="quote-establishment" required placeholder="Nom de l'établissement" value={form.establishment}
              onChange={(e) => setForm({ ...form, establishment: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="quote-contact" required placeholder="Contact (nom)" value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              <Input data-testid="quote-qty" type="number" min={1} max={500} required placeholder="Nb de licences" value={form.qty}
                onChange={(e) => setForm({ ...form, qty: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="quote-email" type="email" required placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="quote-phone" required placeholder="Téléphone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <textarea data-testid="quote-message" rows={3} placeholder="Votre besoin (déploiement, formation, support…)…" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel" />
            <Button data-testid="quote-submit" disabled={busy} className="w-full rounded-full bg-navy py-5 text-sm font-semibold text-white hover:bg-navy-light">
              {busy ? <Loader2 className="animate-spin" size={16} /> : "Envoyer la demande"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PromoButton({ product, compact = false }) {
  const [open, setOpen] = useState(false);
  const { fmt } = useCurrency();
  const adUrl = product.ad_path ? `${API}/files/${product.ad_path}` : null;

  return (
    <>
      {compact ? (
        <button
          data-testid={`promo-btn-${product.product_id}`}
          onClick={() => setOpen(true)}
          title="Voir la publicité"
          className="card-icon-btn flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
        >
          <Megaphone size={15} />
        </button>
      ) : (
        <button
          data-testid="promo-btn"
          onClick={() => setOpen(true)}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-amber-400 bg-amber-50 px-5 py-3 text-sm font-semibold text-amber-600 transition-colors duration-200 hover:bg-amber-100"
        >
          <Megaphone size={16} /> Voir la publicité
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="promo-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Publicité — {product.title}</DialogTitle></DialogHeader>
          {adUrl ? (
            <video key={adUrl} controls autoPlay playsInline className="w-full rounded-lg bg-black" data-testid="promo-player" src={adUrl} />
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200" data-testid="promo-card">
              <img src={coverSrc(product.cover)} alt={product.title} className="max-h-72 w-full object-cover" />
              <div className="bg-navy p-6 text-white">
                <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">{product.category}</p>
                <h3 className="mt-2 font-display text-lg font-bold leading-snug">{product.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-slate-300">{product.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display text-xl font-bold text-sky-400">{fmt(product.price)}</span>
                  <Link to={`/produit/${product.product_id}`} onClick={() => setOpen(false)}
                    className="rounded-full bg-sky-500 px-5 py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-sky-400">
                    Découvrir le produit
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
